import { RefObject, useRef } from "react"

import { useTranslation } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

import Callout from "components/TextElements/Callout"
import { ReactComponent as BlueArcSVG } from "images/homepage/blueArc.svg"
import { ReactComponent as GreenArcSVG } from "images/homepage/greenArc.svg"
import { ReactComponent as MagentaArcSVG } from "images/homepage/magentaArc.svg"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useAnimation from "utils/useAnimation"
import useMedia from "utils/useMedia"

export default function SinglePoint({
  bgRef,
}: {
  bgRef: RefObject<HTMLDivElement> | null
}) {
  const startRef = useRef(null)
  const greenRef = useRef(null)
  const magentaRef = useRef(null)
  const blueRef = useRef(null)

  const { t } = useTranslation()

  useAnimation(() => {
    if (bgRef?.current)
      gsap.to(bgRef.current, {
        background: colors.lightMode,
        scrollTrigger: {
          trigger: startRef.current,
          scrub: true,
          start: "top center",
          end: "bottom center",
        },
      })

    gsap.to(greenRef.current, {
      rotate: 360,
      repeat: -1,
      ease: "linear",
      duration: 1.5,
    })

    gsap.to(magentaRef.current, {
      rotate: -360,
      repeat: -1,
      ease: "linear",
      duration: 1.8,
    })

    gsap.to(blueRef.current, {
      rotate: 360,
      repeat: -1,
      ease: "linear",
      duration: 4.5,
    })
  }, [bgRef])

  return (
    <OuterWrapper ref={startRef}>
      <Container>
        <Rings>
          <GreenArcSVG ref={greenRef} />
          <MagentaArcSVG ref={magentaRef} />
          <BlueArcSVG ref={blueRef} />
        </Rings>

        <BlueDot />
        <CalloutWrapper>
          <Callout
            centered
            variant="white"
            title={{
              content: t(
                "Our single point of integration puts you in the driver’s seat."
              ),
              size: useMedia(text.h3, text.h3, text.h3, text.h6),
              width: "100%",
            }}
            text={{
              width: "100%",
              content: t(
                "Build a customized financial program that's tailored to meet your customers' needs.  And use our simple tools to optimize your business and drive ongoing innovation for profitable growth and maximum impact."
              ),
            }}
            buttons={[
              {
                text: t("Explore the API Guides") ?? "Explore the API Guides",
                arrow: true,
                route: "https://docs.galileo-ft.com/pro/docs",
              },
            ]}
          />
        </CalloutWrapper>
      </Container>
    </OuterWrapper>
  )
}

const OuterWrapper = styled.section`
  width: 100%;
  background-color: transparent;
  display: flex;
  z-index: 1;
  justify-content: center;
  overflow: hidden;
  position: relative;

  ${fresponsive(css`
    padding: 200px 0 300px;
  `)}

  ${ftablet(css`
    padding: 200px 350px;
  `)}

  ${fmobile(css`
    padding: 200px 0 126px;
  `)}
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  ${fresponsive(css`
    width: 949px;
    height: 949px;
  `)}

  ${ftablet(css`
    width: 851px;
    height: 851px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`
const Rings = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  ${ftablet(css`
    width: 949px;
    height: 949px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`
const BlueDot = styled.div`
  border-radius: 50%;
  background-color: ${colors.blue800};

  ${fresponsive(css`
    width: 851px;
    height: 851px;
  `)}

  ${ftablet(css`
    width: 851px;
    height: 851px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`

const CalloutWrapper = styled.div`
  position: absolute;

  ${fresponsive(css`
    width: 627px;
  `)}

  ${fmobile(css`
    width: 335px;
  `)}
`
